import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Settings, FileText } from "lucide-react";

export default function Info() {
  const { isSystemAdmin } = useSimpleRole();
  const navigate = useNavigate();

  const { data: releases, isLoading } = useQuery({
    queryKey: ["release-notes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("release_notes")
        .select("*")
        .eq("is_published", true)
        .order("release_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const headerActions = isSystemAdmin ? (
    <Button 
      variant="outline"
      onClick={() => navigate("/dashboard/info/manage")}
      className="brand-info-manage bg-white border-black text-black hover:bg-slate-50"
    >
      <Settings className="h-4 w-4 mr-2" />
      Manage Releases
    </Button>
  ) : null;

  return (
    <SidebarProvider>
      <div className="brand-portal brand-info-page flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader 
            icon={FileText}
            title="Release Notes"
            description="Latest updates, features, and bug fixes"
            actions={headerActions}
          />
          <div className="p-4 sm:p-6 lg:p-12 xl:px-16" style={{ paddingTop: "8.5rem" }}>
            <div className="w-full max-w-none">

            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="brand-info-card animate-pulse">
                    <CardHeader>
                      <div className="brand-info-placeholder h-6 bg-slate-200 rounded w-1/3 mb-2"></div>
                      <div className="brand-info-placeholder h-4 bg-slate-200 rounded w-1/4"></div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : releases && releases.length > 0 ? (
              <div className="space-y-6">
                {releases.map((release) => (
                  <Card key={release.id} className="brand-info-card border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="brand-info-heading text-2xl text-slate-900">
                            {release.title}
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="brand-info-version font-mono">
                              v{release.version}
                            </Badge>
                            <span className="brand-info-meta text-slate-500">
                              {format(new Date(release.release_date), "MMMM d, yyyy")}
                            </span>
                          </CardDescription>
                        </div>
                      </div>
                      {release.description && (
                        <p className="brand-info-body text-slate-600 mt-4 text-left">{release.description}</p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {release.feature_improvements && 
                       Array.isArray(release.feature_improvements) && 
                       release.feature_improvements.length > 0 && (
                        <div>
                          <h3 className="brand-info-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <span className="text-teal-600">✨</span>
                            Feature Improvements
                          </h3>
                          <ul className="space-y-2">
                            {release.feature_improvements.map((feature: any, idx: number) => (
                              <li key={idx} className="brand-info-body flex items-start gap-2 text-slate-700">
                                <span className="text-teal-500 mt-1">•</span>
                                <span>{feature.description || feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {release.feature_improvements && 
                       release.bug_fixes && 
                       Array.isArray(release.feature_improvements) && 
                       Array.isArray(release.bug_fixes) &&
                       release.feature_improvements.length > 0 && 
                       release.bug_fixes.length > 0 && (
                        <Separator />
                      )}

                      {release.bug_fixes && 
                       Array.isArray(release.bug_fixes) && 
                       release.bug_fixes.length > 0 && (
                        <div>
                          <h3 className="brand-info-heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <span className="text-amber-600">🐛</span>
                            Bug Fixes
                          </h3>
                          <ul className="space-y-2">
                            {release.bug_fixes.map((fix: any, idx: number) => (
                              <li key={idx} className="brand-info-body flex items-start gap-2 text-slate-700">
                                <span className="text-amber-500 mt-1">•</span>
                                <span>{fix.description || fix}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="brand-info-card">
                <CardContent className="py-12 text-center">
                  <p className="brand-info-meta text-slate-500">No release notes available yet.</p>
                </CardContent>
              </Card>
            )}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
